<script setup lang="ts">
import type { PacketPitch } from "#shared/types/universe";
import { KsIcon } from "@aschehoug/kloss";
import { faLongArrowRight } from "@fortawesome/pro-light-svg-icons";
import { tv } from "tailwind-variants";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<PacketPitch & {
  dir: "ltr" | "rtl";
}>();

const packet = inject(keys.currentPacket);

const variants = tv({
  slots: {
    wrapper: "",
    image: "lg:mb-0",
    picture: "z-[1]",
  },
  variants: {
    theme: {
      "green-seagreen": {},
      "dark-red-pink": {},
      "blue-pink": {
        image: "h-full object-cover",
        picture: "-mb-16 aspect-square size-[70%] max-w-[min(60dvw,400px)]",
      },
      "green-red": {},
    },
    dir: {
      ltr: {
        wrapper: "lg:flex-row-reverse",
        picture: "lg:-ml-12",
      },
      rtl: {
        wrapper: "lg:flex-row",
        picture: "lg:-mr-12",
      },
    },
  },
  compoundVariants: [
    {
      theme: ["green-seagreen", "dark-red-pink", "green-red"],
      class: {
        image: "mb-[-75px] object-contain",
        picture: "max-w-[60dvw]",
      },
    },
    {
      theme: "blue-pink",
      dir: "ltr",
      class: {
        image: "rotate-[15deg]",
      },
    },
    {
      theme: "blue-pink",
      dir: "rtl",
      class: {
        image: "rotate-[-15deg]",
      },
    },
  ],
});

const classes = computed(() => variants({ theme: unref(packet)?.theme, dir: props.dir }));

const linkProps = useLinkProps(props.link?.url);
</script>

<template>
  <div
    class="relative flex flex-col items-center"
    :class="classes.wrapper()"
  >
    <CrystallizeImage
      :image="image"
      :class="classes.picture()"
      :img-class="classes.image()"
    />
    <div
      v-bind="$attrs"
      class="flex w-full flex-col gap-m px-m py-l lg:py-[100px] lg:pr-[7.5rem]"
      :class="{
        'pt-[100px] lg:pt-[80px]': !!image?.url,
      }"
    >
      <h3 class="font-headline text-3xl lg:text-6xl" v-text="title" />
      <div v-html="text" />
      <NuxtLink
        v-if="linkProps?.to"
        v-bind="linkProps"
        class="transition-translate flex items-center gap-s text-base leading-8 no-underline duration-300 ease-in-out hover:translate-x-4 lg:text-xl"
      >
        <KsIcon :icon="faLongArrowRight" />
        {{ link?.label || link?.url }}
      </NuxtLink>
    </div>
  </div>
</template>
